// /plan/threats — Module 5 Threats library.
// Mirrors /plan/airframes + /plan/environments UX: scope filter, preset
// rows with clone-to-tenant action, tenant rows with patch/delete, and
// a map preview of the selected threat's forward trajectory.
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Copy, Eye, Route as RouteIcon } from "lucide-react";
import { toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import SpinnerButton from "@/components/SpinnerButton";
import PresetPublishButton from "@/components/PresetPublishButton";
import CredibilityBadge from "@/components/CredibilityBadge";
import { sim } from "@/api/endpoints";
import { useAuth } from "@/auth/AuthContext";
import { presetImmutabilityToast } from "@/lib/presetImmutability";

const MapEditor = lazy(() => import("@/components/terrain/MapEditor"));

const SCOPES = [
    { id: "tenant", label: "All sources" },
    { id: "mine", label: "Mine" },
    { id: "preset", label: "Presets" },
];

const MODE_LABELS = {
    single_waypoint: "Single-ship waypoint",
    swarm: "Swarm",
    evasive: "Evasive",
    comms_denied: "Comms-denied",
};

function ModeChip({ mode }) {
    return (
        <span
            className="inline-flex items-center border border-ux-border-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-ux-text-3"
            data-testid="threat-mode-chip"
        >
            {MODE_LABELS[mode] || mode}
        </span>
    );
}

function agentsToPolylines(preview) {
    const lines = [];
    for (const a of preview?.agents || []) {
        const ring = a.samples.map((s) => [s.lat, s.lon]);
        if (ring.length >= 2) lines.push({ ring, id: a.agent_id });
    }
    return lines;
}

export default function ThreatsPage() {
    const [items, setItems] = useState([]);
    const [scope, setScope] = useState("tenant");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const { user } = useAuth();

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const list = await sim.threats.list({ scope });
            setItems(Array.isArray(list) ? list : []);
        } catch (e) {
            setErr(formatApiError(e, "Failed to load threats"));
        } finally {
            setLoading(false);
        }
    }, [scope]);

    useEffect(() => {
        load();
    }, [load]);

    const selected = useMemo(
        () => items.find((x) => x.id === selectedId) || null,
        [items, selectedId],
    );

    // Fetch preview whenever the selection changes.
    useEffect(() => {
        let alive = true;
        if (!selectedId) {
            setPreview(null);
            return () => {};
        }
        setPreviewLoading(true);
        sim.threats
            .preview(selectedId, { horizon_s: 60, dt_s: 0.25 })
            .then((p) => { if (alive) setPreview(p); })
            .catch(() => { if (alive) setPreview(null); })
            .finally(() => { if (alive) setPreviewLoading(false); });
        return () => { alive = false; };
    }, [selectedId]);

    const onClone = async (id) => {
        setBusyId(id);
        try {
            const cloned = await sim.threats.clone(id);
            toast.success(`Cloned as "${cloned.name}"`);
            await load();
            setSelectedId(cloned.id);
        } catch (e) {
            toast.error(formatApiError(e, "Clone failed"));
        } finally {
            setBusyId(null);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this threat?")) return;
        setBusyId(id);
        try {
            await sim.threats.remove(id);
            toast.success("Threat deleted");
            if (selectedId === id) setSelectedId(null);
            await load();
        } catch (e) {
            presetImmutabilityToast(e, {
                entityType: "threats",
                id,
                onCloned: async () => { await load(); },
            });
        } finally {
            setBusyId(null);
        }
    };

    const polylines = useMemo(() => agentsToPolylines(preview), [preview]);
    const mapCentre = useMemo(() => {
        if (!selected?.spawn) return [51.51, 0.0];
        return [selected.spawn.lat, selected.spawn.lon];
    }, [selected]);

    return (
        <div className="space-y-6" data-testid="threats-page">
            <PageHeader
                title="Threats"
                subtitle="Threat packages — waypoint ingress, swarms, evasive manoeuvres, comms-denied fallbacks."
                actions={
                    <Select value={scope} onValueChange={setScope}>
                        <SelectTrigger
                            className="w-[170px] bg-ux-bg-2 text-[12px] font-mono"
                            data-testid="threats-scope"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                            {SCOPES.map((s) => (
                                <SelectItem
                                    key={s.id}
                                    value={s.id}
                                    className="text-[12px] font-mono"
                                >
                                    {s.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                {/* Left: list of threats */}
                <div
                    className="lg:col-span-2 border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 space-y-2"
                    data-testid="threats-list"
                >
                    <div className="flex items-center justify-between">
                        <h3 className="font-mono text-xs uppercase tracking-wider text-ux-text-3">
                            {items.length} threat{items.length === 1 ? "" : "s"}
                        </h3>
                        <Link
                            to="/plan/airframes"
                            className="text-xs text-ux-accent underline decoration-dotted"
                        >
                            Airframes →
                        </Link>
                    </div>
                    {loading ? (
                        <div className="text-xs text-ux-text-3">Loading…</div>
                    ) : err ? (
                        <div
                            className="text-xs text-ux-danger"
                            data-testid="threats-list-error"
                        >
                            {err}
                        </div>
                    ) : items.length === 0 ? (
                        <EmptyState
                            title="No threats yet"
                            description="Create a threat from the Workbench, or clone a preset."
                        />
                    ) : (
                        <ul className="space-y-1">
                            {items.map((it) => {
                                const active = it.id === selectedId;
                                const count = Math.round((it.count || {}).value || 1);
                                return (
                                    <li
                                        key={it.id}
                                        className={`group flex items-start gap-2 px-2 py-2 rounded-md border ${
                                            active
                                                ? "border-ux-accent/60 bg-ux-accent/5"
                                                : "border-ux-border-1 hover:border-ux-border-2"
                                        }`}
                                        data-testid={`threat-row-${it.id}`}
                                    >
                                        <button
                                            type="button"
                                            className="flex-1 text-left"
                                            onClick={() => setSelectedId(it.id)}
                                            data-testid={`threat-select-${it.id}`}
                                        >
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="font-medium">{it.name}</span>
                                                <ModeChip mode={it.mode} />
                                                <CredibilityBadge entity={it} testId={`threat-tier-${it.id}`} />
                                                {it.is_preset ? (
                                                    <span className="text-[10px] font-mono uppercase tracking-wider text-ux-text-3">
                                                        preset
                                                    </span>
                                                ) : null}
                                            </div>
                                            <div className="text-[11px] font-mono text-ux-text-3">
                                                {count}-ship · airframe {it.airframe_id}
                                            </div>
                                        </button>
                                        {it.is_preset ? (
                                            <SpinnerButton
                                                size="sm"
                                                variant="ghost"
                                                loading={busyId === it.id}
                                                onClick={() => onClone(it.id)}
                                                testId={`threat-clone-${it.id}`}
                                                title="Clone to my tenant"
                                                aria-label="Clone to my tenant"
                                            >
                                                <Copy className="h-3.5 w-3.5" />
                                            </SpinnerButton>
                                        ) : (
                                            <>
                                                <PresetPublishButton
                                                    entityType="threats"
                                                    entityId={it.id}
                                                    entity={it}
                                                    user={user}
                                                    size="sm"
                                                    variant="ghost"
                                                    label=""
                                                    testId={`threat-publish-${it.id}`}
                                                    onPublished={load}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => onDelete(it.id)}
                                                    className="text-ux-text-3 hover:text-ux-danger"
                                                    data-testid={`threat-delete-${it.id}`}
                                                    aria-label="Delete"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {/* Right: map preview */}
                <div className="lg:col-span-3 space-y-3">
                    <div
                        className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 flex items-center gap-3"
                        data-testid="threats-preview-header"
                    >
                        <RouteIcon className="h-4 w-4 text-ux-text-3" />
                        <span className="text-xs font-mono uppercase tracking-wider text-ux-text-3">
                            Preview · first 60 s
                        </span>
                        {previewLoading ? (
                            <span className="text-xs text-ux-text-3">computing…</span>
                        ) : preview ? (
                            <span className="text-xs text-ux-text-3 font-mono">
                                {preview.agent_count} agent{preview.agent_count === 1 ? "" : "s"}
                                {" · "}
                                {(preview.agents?.[0]?.samples?.length || 0)} ticks
                            </span>
                        ) : null}
                        {selected?.mode ? (
                            <div className="ml-auto flex items-center gap-2">
                                <Eye className="h-3.5 w-3.5 text-ux-text-3" />
                                <span className="text-[11px] font-mono uppercase text-ux-text-3">
                                    {MODE_LABELS[selected.mode] || selected.mode}
                                </span>
                            </div>
                        ) : null}
                    </div>

                    <Suspense
                        fallback={
                            <div
                                data-testid="threats-map-loading"
                                className="h-[420px] rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center text-xs text-slate-500"
                            >
                                Loading map…
                            </div>
                        }
                    >
                        <MapEditor
                            centre={mapCentre}
                            zoom={selected ? 13 : 4}
                            losLine={null}
                            obstacles={[]}
                            heightPx={420}
                            testId="threats-map"
                            polylines={polylines}
                        />
                    </Suspense>
                </div>
            </div>
        </div>
    );
}
